<template>
  <section>
    <errors-list :errorMessage="errorMessage" />

    <v-data-table
      :headers="translatedHeaders"
      :items="items"
      :rows-per-page-items="rowsPerPageItems"
      :loading="loading"
      :total-items="pagination.totalItems"
      :pagination.sync="pagination"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <grid-cell
            :header="header"
            :row="props.item"
            v-for="(header, index) in translatedHeaders"
            :key="index"
          ></grid-cell>
        </tr>
      </template>
    </v-data-table>
  </section>
</template>

<script>
import ErrorsList from '../../components/ErrorsList';
import GridCell from '../../components/GridCell';
import { getErrorMessage } from '../../errorHandlers';
import { defaultCostFormatter, defaultDateTimeFormatter, serializeQuery } from '../../utils';
import { BALANCE_CORRECTION_PROCESSED } from '../../wsEventTypes';

const TRANSACTION_TYPE_LABELS = {
  billing: 'Billing',
  invoicePaid: 'Invoice paid',
  contractTerminated: 'Contract terminated',
  balanceCorrection: 'Balance correction',
  campaignDiscount: 'Campaign discount',
};

const defaultHeaders = [
  {
    text: '@transactionsList/typeColumnHeader',
    value: 'reason',
    format: (val) => TRANSACTION_TYPE_LABELS[val] || 'Unknown',
  },
  {
    text: '@transactionsList/sumColumnHeader',
    value: 'sum',
    format: (val) => val && defaultCostFormatter(val),
  },
  {
    text: '@transactionsList/balanceBeforeColumnHeader',
    value: 'before.contractBalance',
    format: (val) => val && defaultCostFormatter(val),
    sortable: false,
  },
  {
    text: '@transactionsList/balanceAfterColumnHeader',
    value: 'after.contractBalance',
    format: (val) => val && defaultCostFormatter(val),
    sortable: false,
  },
  {
    text: '@transactionsList/dateColumnHeader',
    value: 'createdAt',
    format: defaultDateTimeFormatter,
  },
];

export default {
  components: { GridCell, ErrorsList },

  props: {
    headers: {
      type: Array,
      default: () => defaultHeaders,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      errorMessage: '',
      loading: false,
      rowsPerPageItems: [10, 15, 50, 100],
      items: [],
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 15,
        sortBy: 'createdAt',
        totalItems: 0,
      },
    };
  },

  methods: {
    async fetch(force) {
      const qs = serializeQuery(this.query);

      if (!force && (this.lastQueryString === qs || this.loading)) {
        return;
      }

      try {
        this.loading = true;
        this.$emit('beforeRequest', this.query);
        const { skip, take } = this.query;
        const { data } = await this.$http.get(`/api/transactions/insurances?${qs}`);
        this.items = data || [];
        this.lastQueryString = qs;
        if (data.length === Number(take)) {
          this.pagination.totalItems = skip + take + 1;
          this.pagination.skip = take;
        }
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },
  },

  watch: {
    filters() {
      this.fetch();
    },
    pagination() {
      this.fetch();
    },
  },

  async created() {
    await this.fetch();
  },

  computed: {
    translatedHeaders() {
      return Array.isArray(this.headers)
        ? this.headers.map((h) => ({
            ...h,
            text: this.$t(h.text),
          }))
        : [];
    },
    query() {
      const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const take = this.pagination.rowsPerPage;

      const query = {
        ...this.filters,
        skip,
        take,
      };

      if (this.pagination.sortBy) {
        query.sortBy = `tran.${this.pagination.sortBy}`;
        query.sortOrder = this.pagination.descending ? 'DESC' : 'ASC';
      } else {
        delete query.sortBy;
        delete query.sortOrder;
      }

      query.from = query.from || '1970-01-01T00:00:00.000Z';

      return query;
    },
  },

  sockets: {
    insuranceSubscribe: function(data) {
      const eventTypes = [BALANCE_CORRECTION_PROCESSED];
      if (
        data.meta &&
        data.meta.contractId === this.filters.contractId &&
        eventTypes.includes(data.type)
      ) {
        this.fetch(true);
      }
    },
  },
};
</script>

<style scoped>
.status-message {
  color: red;
}
</style>
